import { useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import type { BreedingInventorySnapshot } from "../breeding/liveBreedingInventory";
import {
  buildPairMoveSummary,
  eligiblePairMoveDestinations,
  pairCurrentTank,
  pairHasActiveCycle,
  validatePairMoveSelection,
} from "../breeding/pairMove";
import { buildPairMoveOperation } from "../operations/breedingInventoryOperations";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { ConfirmationSheet } from "./ConfirmationSheet";
import { speakPersian } from "./audioGuidance";

interface PairMovePanelProps {
  farmId: string;
  userId: string;
  snapshot: BreedingInventorySnapshot;
  pairId: string;
  onSyncSummary?: (summary: OutboxSummary) => void;
  onCompleted: () => void;
}

function pairMoveError(code?: string): string {
  const messages: Record<string, string> = {
    PAIR_NOT_ACTIVE: "این جفت دیگر فعال نیست. اطلاعات سالن را به‌روزرسانی کنید.",
    PAIR_OCCUPANCY_MISSING: "تانک فعلی جفت مشخص نیست. وضعیت سالن باید بررسی شود.",
    PAIR_ACTIVE_CYCLE_EXISTS:
      "این جفت چرخه تکثیر فعال دارد. برای جابه‌جایی مرتبط با بچه‌ها از «جداسازی و انتقال» استفاده کنید.",
    PAIR_DESTINATION_REQUIRED: "تانک مقصد را انتخاب کنید.",
    ALREADY_IN_TARGET_TANK: "تانک مقصد همان تانک فعلی جفت است.",
    TARGET_TANK_INVALID: "تانک مقصد دیگر فعال یا در دسترس نیست.",
    TARGET_TANK_OCCUPIED:
      "تانک مقصد خالی نیست یا در همین فاصله اشغال شده است. اطلاعات سالن را به‌روزرسانی کنید.",
    TARGET_TANK_PAIR_OCCUPIED: "تانک مقصد در همین فاصله توسط جفت دیگری اشغال شده است.",
    ENTITY_VERSION_CHANGED: "وضعیت جفت تغییر کرده است. صفحه را به‌روزرسانی و دوباره تلاش کنید.",
    PERMISSION_DENIED: "شما اجازه جابه‌جایی جفت را ندارید.",
  };
  return code
    ? (messages[code] ?? "جابجایی جفت توسط سرور تأیید نشد. اطلاعات را به‌روزرسانی کنید.")
    : "جابجایی جفت انجام نشد.";
}

export function PairMovePanel({
  farmId,
  userId,
  snapshot,
  pairId,
  onSyncSummary,
  onCompleted,
}: PairMovePanelProps) {
  const pair = snapshot.pairs.find((item) => item.id === pairId && item.status === "active");
  const sourceTank = pairCurrentTank(snapshot, pairId);
  const activeCycle = pairHasActiveCycle(snapshot, pairId);
  const destinations = useMemo(
    () => eligiblePairMoveDestinations(snapshot, pairId),
    [snapshot, pairId],
  );
  const [targetTankId, setTargetTankId] = useState(destinations[0]?.id ?? "");
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!destinations.some((tank) => tank.id === targetTankId)) {
      setTargetTankId(destinations[0]?.id ?? "");
    }
  }, [destinations, targetTankId]);

  function requestConfirmation() {
    setMessage(undefined);
    setError(undefined);

    const validation = validatePairMoveSelection(snapshot, pairId, targetTankId);
    if (!validation.ok) {
      const text = pairMoveError(validation.code);
      setError(text);
      speakPersian(text);
      return;
    }

    const operation = buildPairMoveOperation(
      {
        actorId: userId,
        farmId,
        deviceId: getOrCreateDeviceId(),
        performedAt: new Date().toISOString(),
      },
      {
        pairId: validation.pair.id,
        pairVersion: validation.pair.domainVersion,
        targetTankId: validation.targetTank.id,
      },
    );

    setPending(operation);
    const parts = buildPairMoveSummary({
      pairCode: validation.pair.pairCode,
      sourceTankCode: validation.sourceTank.tankCode,
      targetTankCode: validation.targetTank.tankCode,
    });
    if (activeCycle) {
      parts.push("چرخه تکثیر فعال است؛ پس از انتقال، جداسازی والدین را ثبت کنید");
    }
    setConfirmationParts(parts);
  }

  async function confirm() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);

      const online = typeof navigator === "undefined" ? true : navigator.onLine !== false;
      const result = await persistAndSyncOperation(supabase, pending, online);
      onSyncSummary?.(result.summary);
      setPending(undefined);

      if (result.operation.status === "synced") {
        const text = "جابجایی جفت ثبت شد و تانک جدید به‌روزرسانی شد.";
        setMessage(text);
        speakPersian(text);
        onCompleted();
        return;
      }

      if (result.operation.status === "terminal_conflict") {
        const text = pairMoveError(result.operation.lastErrorCode);
        setError(text);
        speakPersian(text);
        return;
      }

      const text =
        "جابجایی به‌صورت محلی ثبت شد و در صف همگام‌سازی است؛ وضعیت قطعی پس از تأیید سرور مشخص می‌شود.";
      setMessage(text);
      speakPersian(text);
    } catch (caught) {
      const text = caught instanceof Error ? caught.message : "ثبت جابجایی جفت انجام نشد.";
      setError(text);
    } finally {
      setBusy(false);
    }
  }

  if (!pair) {
    return (
      <article className="manager-card settings-warning">
        {pairMoveError("PAIR_NOT_ACTIVE")}
      </article>
    );
  }

  if (!sourceTank) {
    return (
      <article className="manager-card settings-warning">
        {pairMoveError("PAIR_OCCUPANCY_MISSING")}
      </article>
    );
  }

  return (
    <article className="manager-card">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">جابجایی جفت</p>
          <h3>{pair.pairCode}</h3>
        </div>
        <span className="muted-chip">مبدأ: {sourceTank.tankCode}</span>
      </div>

      <button
        type="button"
        className="secondary-button large-button"
        onClick={() =>
          speakPersian(
            `جفت ${pair.pairCode} اکنون در تانک ${sourceTank.tankCode} است. فقط تانک‌های کاملاً خالی برای مقصد نمایش داده می‌شوند.`,
          )
        }
      >
        🔊 راهنمای جابجایی
      </button>

      {destinations.length === 0 ? (
        <p className="settings-warning">هیچ تانک فعال و کاملاً خالی برای مقصد وجود ندارد.</p>
      ) : (
        <div className="operation-field-grid">
          <label>
            تانک فعلی
            <input value={sourceTank.tankCode} readOnly />
          </label>
          <label>
            تانک مقصد
            <select
              value={targetTankId}
              onChange={(event) => {
                setTargetTankId(event.target.value);
                const target = destinations.find((tank) => tank.id === event.target.value);
                if (target) speakPersian(`تانک مقصد ${target.tankCode}`);
              }}
            >
              {destinations.map((tank) => (
                <option value={tank.id} key={tank.id}>
                  {tank.tankCode}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <p className="section-note">
        در جابجایی جفت، کد جفت، مولد نر و ماده و سابقه تکثیر تغییر نمی‌کند؛ فقط محل استقرار جفت عوض
        می‌شود.
      </p>

      {activeCycle ? (
        <p className="settings-warning">
          این جفت چرخه تکثیر فعال دارد. جابجایی فقط محل جفت را تغییر می‌دهد؛ پس از انتقال، وضعیت
          جداسازی والدین را از «جداسازی و انتقال» ثبت کنید.
        </p>
      ) : null}

      <button
        type="button"
        className="primary-button large-button"
        disabled={busy || destinations.length === 0}
        onClick={requestConfirmation}
      >
        {busy ? "در حال ثبت…" : "بررسی و جابجایی"}
      </button>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید جابجایی جفت"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirm()}
        />
      ) : null}
    </article>
  );
}
